import {Elements} from '@stripe/react-stripe-js'
import {type StripeElementsOptionsMode} from '@stripe/stripe-js'
import {loadStripe} from '@stripe/stripe-js/pure'
loadStripe.setLoadParameters({advancedFraudSignals: false})

import {STRIPE_PK} from '@/constants/config'
import {currencyAtom} from '@/lib/locale/country'
import {Theme} from '@/lib/theme/theme'
import {currentThemeAtom} from '@/lib/theme/useCurrentTheme'
import {createCssVariableAtom} from '@/utils/components/CssVariableChange'

const elementsOptionsAtom = atomWithMutative<StripeElementsOptionsMode>({
  appearance: {
    variables: {
      fontFamily: 'Manrope, "Manrope fallback", sans-serif',
      spacingUnit: '0.3125rem',
      borderRadius: '0.5rem',
      focusBoxShadow: 'none',
    },
    rules: {
      '.Label': {
        fontWeight: '700',
      },
      '.Input': {
        boxShadow: 'none',
        borderWidth: '2px',
      },
      '.Tab': {
        boxShadow: 'none',
      },
      '.AccordionItem': {
        boxShadow: 'none',
      },
    },
  },
  loader: 'always',
  paymentMethodCreation: 'manual',
})
elementsOptionsAtom.debugLabel = 'elementsOptionsAtom'

const primaryColorAtom = createCssVariableAtom('--Primary2')
const backgroundColorAtom = createCssVariableAtom('--Input-Boxfill-Default')
const textColorAtom = createCssVariableAtom('--Input-Content-Default')

const elementOptionsAppearanceEffect = atomEffect((get, set) => {
  const primaryColor = get(primaryColorAtom)
  const backgroundColor = get(backgroundColorAtom)
  const textColor = get(textColorAtom)
  const currentTheme = get(currentThemeAtom)

  set(elementsOptionsAtom, draft => {
    draft.appearance ??= {}
    draft.appearance.variables ??= {}
    draft.appearance.theme = currentTheme === Theme.Dark ? 'night' : 'stripe'
    draft.appearance.variables.colorPrimary = primaryColor
    draft.appearance.variables.colorBackground = backgroundColor
    draft.appearance.variables.colorText = textColor
  })
})

const elementOptionsCurrencyEffect = atomEffect((get, set) => {
  const currency = get(currencyAtom)
  set(elementsOptionsAtom, draft => {
    draft.currency = currency ? currency.toLowerCase() : undefined
  })
})

export function useSetElementsOptions() {
  return useSetAtom(elementsOptionsAtom)
}

export function useElementsOptions() {
  return useAtomValue(elementsOptionsAtom)
}

export default deepMemo(function PaymentProvider({children}: PropsWithChildren) {
  useAtom(elementOptionsAppearanceEffect)
  useAtom(elementOptionsCurrencyEffect)

  const stripePromise = useMemo(() => loadStripe(STRIPE_PK), [])

  const options = useElementsOptions()
  const modifiedOptions = useMemo(() => {
    const newAmount = (() => {
      if (!options.mode || !options.amount || options.amount <= 0) return 100
      return options.amount
    })()

    return {
      captureMethod: 'manual',
      mode: 'payment',
      ...options,
      amount: newAmount,
    } as const
  }, [options])

  return (
    <Elements stripe={stripePromise} options={modifiedOptions}>
      {children}
    </Elements>
  )
})
